import axios from 'axios'

import { type ServerResponse, normalizeToPrimaryLanguages } from '~/axios'
import { type PlatformLanguage } from '~/utils/types/i18n'

interface RecentActivity {
  id: number
  activityType: ActivityType
  params: string[]
  username: string
  createdAt: string
  familyId: number | null
  familyName: null | string
  stoplightClient: StoplightClient
  message: string
  referenceId: number
}

enum ActivityType {
  BaselineSurvey = 'BASELINE_SURVEY',
  NewStoplightPriority = 'NEW_STOPLIGHT_PRIORITY',
  NewStoplightSolution = 'NEW_STOPLIGHT_SOLUTION',
}

enum StoplightClient {
  Mobile = 'MOBILE',
  MobileAndroid = 'MOBILE_ANDROID',
  Web = 'WEB',
}
export async function getLastestActivity() {
  return await axios.post<ServerResponse<{ recentActivity: RecentActivity[] }>>(
    '/graphql',
    JSON.stringify({
      query:
        'query { recentActivity { id, activityType, params, username, createdAt, familyId, familyName, stoplightClient, message, referenceId } }',
    }),
  )
}

export async function checkIdenticalSurveys(surveyIds: number[]) {
  /* TODO: Type this */
  return await axios.post<ServerResponse<{ verifyUnifiableSurveys: boolean }>>(
    '/graphql',
    JSON.stringify({
      query:
        'query verifyUnifiableSurveys($surveyIds: [Long]) {verifyUnifiableSurveys(surveyIds: $surveyIds)}',
      variables: {
        surveyIds,
      },
    }),
  )
}

export interface TotalFamilies {
  peopleCount: number
  familiesCount: number
  snapshotsCount: number
  followupsCount: number
  newFamiliesCount: number | null
  continuingFamiliesCount: number | null
  peopleWithStoplightCount: number
  familiesWithStoplightCount: number
  snapshotsWithoutStoplight: number
}

export async function getTotalFamilies(
  hubId: number | null,
  organizationIds: any[],
  surveyIds: number[],
  projectIds: number[],
  fromTime: number | null,
  toTime: number | null,
  language: PlatformLanguage,
) {
  const query = /* GraphQL */ `
    query totalFamilies(
      $hub: Long
      $toTime: Long
      $fromTime: Long
      $surveys: [Long]
      $projects: [Long]
      $organizations: [Long]
    ) {
      totalFamilies(
        hub: $hub
        toTime: $toTime
        surveys: $surveys
        projects: $projects
        fromTime: $fromTime
        organizations: $organizations
      ) {
        peopleCount
        familiesCount
        snapshotsCount
        followupsCount
        newFamiliesCount
        continuingFamiliesCount
        peopleWithStoplightCount
        snapshotsWithoutStoplight
        familiesWithStoplightCount
      }
    }
  `

  return await axios.post<ServerResponse<{ totalFamilies: TotalFamilies }>>(
    '/graphql',
    {
      query,
      variables: {
        toTime,
        fromTime,
        hub: hubId,
        surveys: surveyIds,
        projects: projectIds,
        organizations: organizationIds,
      },
    },
    {
      headers: {
        'X-locale': normalizeToPrimaryLanguages(language),
      },
    },
  )
}

export interface BlockOverview {
  stoplightOverview: StoplightOverview
  priorities: number
  achievements: number
}

export interface StoplightOverview {
  greens: number
  yellows: number
  reds: number
  skipped: number
  doesNotApply: number
}

// TODO: Type these values
export async function getOverviewBlock(
  hub: number | null,
  fromDate: number | null,
  toDate: number | null,
  organizations: any[],
  surveys: any[],
  projects: any[],
  snapshotNumber: number,
  lang: PlatformLanguage,
) {
  return await axios.post<ServerResponse<{ blockOverview: BlockOverview }>>(
    '/graphql',
    JSON.stringify({
      query:
        'query blockOverview($hub: Long, $organizations: [Long], $surveys: [Long], $projects: [Long], $snapshotNumber: Long, $toDate: Long, $fromDate: Long) { blockOverview(hub: $hub, organizations: $organizations,surveys: $surveys, projects: $projects, snapshotNumber:$snapshotNumber, toDate: $toDate, fromDate: $fromDate) { stoplightOverview{ greens yellows reds skipped doesNotApply} priorities achievements } }',
      variables: {
        hub,
        organizations,
        surveys,
        projects,
        snapshotNumber,
        fromDate,
        toDate,
      },
    }),
    {
      headers: {
        'X-locale': normalizeToPrimaryLanguages(lang),
      },
    },
  )
}

export interface EconomicOverview {
  familiesCount: number
  peopleCount: number
  familiesWithStoplightCount: number
  peopleByCountries: PeopleByCountry[]
  snapshotsCount: number
  followupsCount: number
  membersAverage: string
  genders: Genders
}

export interface Genders {
  male: number
  female: number
  others: Record<string, number>
}
export interface PeopleByCountry {
  country: string | null
  people: number
}

export async function getEconomicOverview(
  hub: number | null,
  fromDate: number | null,
  toDate: number | null,
  organizationIds: number[],
  surveyIds: number[],
  projectIds: number[],
  snapshotNumber: number,
  lang: PlatformLanguage,
) {
  const query = /* GraphQL */ `
    query economicOverview(
      $hub: Long
      $organizations: [Long]
      $surveys: [Long]
      $projects: [Long]
      $snapshotNumber: Long
      $toDate: Long
      $fromDate: Long
    ) {
      economicOverview(
        hub: $hub
        organizations: $organizations
        surveys: $surveys
        projects: $projects
        snapshotNumber: $snapshotNumber
        toDate: $toDate
        fromDate: $fromDate
      ) {
        familiesCount
        peopleCount
        familiesWithStoplightCount
        peopleByCountries {
          country
          people
        }
        snapshotsCount
        followupsCount
        membersAverage
        genders {
          male
          female
          others
        }
      }
    }
  `

  return await axios<ServerResponse<{ economicOverview: EconomicOverview }>>({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: {
        hub,
        organizations: organizationIds,
        surveys: surveyIds,
        projects: projectIds,
        snapshotNumber,
        fromDate,
        toDate,
      },
    },
    headers: {
      'X-locale': normalizeToPrimaryLanguages(lang),
    },
  })
}
export interface DimensionIndicator {
  dimension: string
  priorities: number
  achievements: number
  dimensionUrl: string
  stoplights: Stoplight[]
  indicators: Indicator[]
}

export interface Indicator {
  name: string
  icon?: string
  dimension: string
  priorities: number
  achievements: number
  stoplights: Stoplight[]
}

export interface Stoplight {
  count: number
  color: number
  dimension: Record<string, string>
  indicator?: string
}

export interface DimensionIndicatorsResponse {
  dimensionIndicators: DimensionIndicator[]
}

export async function getDimensionIndicators(
  hub: number | null,
  organizations: number[],
  surveys: number[],
  projects: number[],
  fromDate: number | null,
  toDate: number | null,
  snapshotNumber: number,
  lang: PlatformLanguage,
  unified: boolean,
) {
  const query = /* GraphQL */ `
    query dimensionIndicators(
      $hub: Long
      $organizations: [Long]
      $surveys: [Long]
      $projects: [Long]
      $snapshotNumber: Long
      $fromDate: Long
      $toDate: Long
      $unified: Boolean
    ) {
      dimensionIndicators(
        hub: $hub
        organizations: $organizations
        surveys: $surveys
        projects: $projects
        snapshotNumber: $snapshotNumber
        fromDate: $fromDate
        toDate: $toDate
        unified: $unified
      ) {
        dimension
        priorities
        achievements
        dimensionUrl
        stoplights {
          count
          color
          dimension
          indicator
        }
        indicators {
          name
          dimension
          achievements
          priorities
          stoplights {
            count
            color
            dimension
            indicator
          }
        }
      }
    }
  `
  return await axios.post<ServerResponse<DimensionIndicatorsResponse>>(
    '/graphql',
    {
      query,
      variables: {
        hub,
        organizations,
        surveys,
        projects,
        snapshotNumber,
        fromDate,
        toDate,
        unified,
      },
    },
    {
      headers: {
        'X-locale': normalizeToPrimaryLanguages(lang),
      },
    },
  )
}

interface OperationsByMonth {
  operationsOverview: { surveysByMonth: Record<string, number> | null }
}

export async function getOperationsOverview(
  hub: number | null,
  fromDate: number | null,
  toDate: number | null,
  organizations: any[],
  surveys: any[],
  projects: any[],
  lang: PlatformLanguage,
) {
  return await axios.post<ServerResponse<OperationsByMonth>>(
    '/graphql',
    JSON.stringify({
      query:
        'query operationsOverview($hub: Long, $organizations: [Long], $surveys: [Long], $projects: [Long], $toTime: Long, $fromTime: Long) { operationsOverview(hub: $hub, organizations: $organizations,surveys: $surveys, projects: $projects, toTime: $toTime, fromTime: $fromTime) { surveysByMonth } }',
      variables: {
        hub,
        organizations,
        surveys,
        projects,
        toTime: toDate,
        fromTime: fromDate,
      },
    }),
    {
      headers: {
        'X-locale': normalizeToPrimaryLanguages(lang),
      },
    },
  )
}
